import React from 'react';
import Icon from '../../../components/AppIcon';

const ProcessSection = () => {
  const processSteps = [
    {
      number: "01",
      title: "Discovery & Strategy",
      description: "We start with deep understanding—your business goals, user needs, and technical constraints. I ask the hard questions to ensure we're solving the right problems.",
      icon: "Search",
      deliverables: ["Requirements document", "Technical feasibility analysis", "Project timeline", "Cost estimation"]
    },
    {
      number: "02",
      title: "Architecture & Planning",
      description: "Before writing code, I design the system architecture that will support your growth. This phase prevents costly rewrites and ensures scalability from day one.",
      icon: "Layout",
      deliverables: ["System architecture diagram", "Database schema", "API specifications", "Technology stack selection"]
    },
    {
      number: "03",
      title: "Iterative Development",
      description: "I build in sprints with regular check-ins, so you see progress weekly. This approach allows for course corrections and ensures the final product matches your vision.",
      icon: "Code2",
      deliverables: ["Working prototypes", "Weekly progress updates", "Feature demonstrations", "Code reviews"]
    },
    {
      number: "04",
      title: "Testing & Refinement",
      description: "Rigorous testing across devices, browsers, and use cases. I don't just test if it works—I test if it works under real-world conditions and edge cases.",
      icon: "CheckCircle2",
      deliverables: ["Test coverage reports", "Performance benchmarks", "Security audit", "Bug fixes"]
    },
    {
      number: "05",
      title: "Deployment & Launch",
      description: "Smooth deployment with zero downtime. I handle all technical aspects of going live, including monitoring setup and rollback plans for peace of mind.",
      icon: "Rocket",
      deliverables: ["Production deployment", "Monitoring setup", "Documentation", "Training materials"]
    },
    {
      number: "06",
      title: "Support & Evolution",
      description: "Launch is just the beginning. I provide ongoing support, performance monitoring, and strategic guidance as your product grows and evolves.",
      icon: "LifeBuoy",
      deliverables: ["Performance monitoring", "Bug fixes", "Feature enhancements", "Technical consultation"]
    }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Icon name="Settings" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">How I Work</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4 md:mb-6">
            My Development Process
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            A proven methodology that balances speed with quality, ensuring you get production-ready solutions without surprises or delays.
          </p>
        </div>

        <div className="space-y-8 md:space-y-12">
          {processSteps?.map((step, index) => (
            <div
              key={index}
              className="group relative bg-card rounded-2xl p-6 md:p-8 lg:p-10 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl"
            >
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                      <Icon name={step?.icon} size={32} className="text-primary md:w-10 md:h-10" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary flex items-center justify-center shadow-lg">
                      <span className="text-sm md:text-base font-bold text-primary-foreground">{step?.number}</span>
                    </div>
                  </div>
                </div>

                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="text-xl md:text-2xl lg:text-3xl font-bold font-heading text-foreground mb-2">
                      {step?.title}
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                      {step?.description}
                    </p>
                  </div>

                  <div className="bg-background rounded-xl p-4 md:p-6 border border-border">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="Package" size={18} className="text-primary" />
                      <span className="text-sm font-medium text-foreground">Key Deliverables</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {step?.deliverables?.map((deliverable, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{deliverable}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 md:p-12 border border-primary/20">
          <div className="text-center max-w-3xl mx-auto">
            <Icon name="MessageSquare" size={48} className="text-primary mx-auto mb-6" />
            <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground mb-4">
              Communication is Key
            </h3>
            <p className="text-base md:text-lg text-muted-foreground mb-6">
              Throughout every phase, you'll have direct access to me. No project managers, no communication barriers—just clear, honest updates about progress, challenges, and next steps.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background border border-border">
                <Icon name="Clock" size={18} className="text-primary" />
                <span className="text-sm text-foreground">24-hour response time</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background border border-border">
                <Icon name="Video" size={18} className="text-primary" />
                <span className="text-sm text-foreground">Weekly video check-ins</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background border border-border">
                <Icon name="FileText" size={18} className="text-primary" />
                <span className="text-sm text-foreground">Detailed documentation</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;